import { ActionMethod, AssetType } from '../../../api/models/Shared.types';
import { Lightning, Log, Router } from '@lightningjs/sdk';
import { VodApi } from '../../../api/VodApi';
import { PriceApi } from '../../../api/price';
import { VodStreamRights } from '../../detail/vod/VodStreamRights';
import { VodAsset } from '../../detail/vod/VodAsset';
import { ErrorTranslationKeys } from '../../../api/models/TranslationKeys';
import { BookmarkUtility } from '../../utility/BookmarkUtility';
import { VodStreamInfo } from '../../player/streamInfo/VodStreamInfo';
import { EpgEvent, StartPositionType, StreamInfo } from '../../player/streamInfo/StreamInfo';
import { EpgUtil } from '../../utility/EpgUtil';
import { timeStringToMs } from '../../utility/DateFormatter';
import { ReplayStreamInfo } from '../../player/streamInfo/ReplayStreamInfo';
import { LinearStreamInfo } from '../../player/streamInfo/LinearStreamInfo';
import { ChannelModel } from '../../../api/EpgApi.types';
import { BlackoutUtil } from '../../utility/BlackoutUtil';
import { NpvrStreamInfo } from '../../player/streamInfo/NpvrStreamInfo';
import { IAssetAction, IScheduleV5 } from '../../../api/models/AndromeTV.types';
import { Backend } from '../../../api/Backend';
import { Recording } from '../../../api/user/RecordingApi.types';
import { ChannelManager } from '../../../api/ChannelManager';
import { EpgApiV5 } from '../../../api/EpgApiV5';

export function resolvePlay(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
  if (action.method !== ActionMethod.PLAY) {
    Log.error('[resolvePlay]', 'wrong method');
    return;
  }
  switch (action.type) {
    case AssetType.VOD:
      return playVod(caller, action, backend);
    case AssetType.PROGRAM:
      return playProgram(caller, action, backend);
    case AssetType.CHANNEL:
      return playChannel(caller, action, backend);
    case AssetType.SCHEDULE:
      return playSchedule(caller, action, backend);
    case AssetType.RECORDING:
      return playRecording(caller, action, backend);
    case AssetType.BUNDLE:
    case AssetType.SERIES:
    case AssetType.ARTICLE:
    case AssetType.PRODUCT:
    case AssetType.SUBSCRIPTION:
    case AssetType.PAGE:
      // TODO handle error?
      Log.error('[CmsActionResolver]', '[resolvePlay]', 'not  an implemented assettype: ', action.type);
  }
}

function playVod(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
  backend.cachedQuoteApi.bypassCache(true);
  return Promise.all([
    VodApi.asset(backend, action.id),
    backend.cachedQuoteApi.list(backend),
    PriceApi.forVod(backend, action.id, {
      entitlementId: backend.sessionInfo?.device.entitlementId,
      catalogId: backend.deviceProfile?.profile.catalog,
    }),
  ]).then(([asset, quotes, price]) => {
    const vodStreamRights = new VodStreamRights(asset.assetId, quotes, price);
    const vodAsset = new VodAsset(asset);

    if (!backend.isAuthorized() && !vodStreamRights.hasWatchableOttFVOD()) {
      //TODO show login
      return;
    }

    if (!vodStreamRights.hasWatchableOttTVOD() && vodAsset.isExpired()) {
      showError(caller, backend, ErrorTranslationKeys.error_vod_expired);
      return;
    }

    const stream = vodStreamRights.getWatchableOttStream();

    if (!stream || !stream.getStreamType() || !stream.getStreamId()) {
      showError(caller, backend, ErrorTranslationKeys.error_no_play_rights_vod);
      return;
    }

    backend.cachedBookmarkApi.bypassCache(true);
    return backend.cachedBookmarkApi
      .bookmark(
        backend,
        {
          type: AssetType.VOD,
          id: vodAsset.assetId,
        },
        false
      )
      .then(bookmark => {
        const fromBookmark = bookmark && BookmarkUtility.isPartial(backend, bookmark);
        const streamInfo = new VodStreamInfo(
          vodAsset,
          stream,
          bookmark,
          fromBookmark ? StartPositionType.BOOKMARK : StartPositionType.START
        );
        if (checkAdult()) {
          Router.navigate('play', { streamInfo });
        }
      });
  });
}

function playProgram(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
  if (!backend.isAuthorized()) {
    // todo show login
    return;
  }

  const fromUntil = EpgUtil.epgFromUntil(backend);

  return Promise.all([
    ChannelManager.channels(backend),
    EpgApiV5.program(backend, action.id),
    EpgApiV5.programLineup(backend, action.id, fromUntil.from, fromUntil.until),
  ]).then(([channels, program, programLineup]) => {
    const schedule = EpgUtil.findClosestSchedule(programLineup.schedules, true, channels);

    if (!schedule) {
      showError(caller, backend, ErrorTranslationKeys.error_general);
      return;
    }

    if (timeStringToMs(schedule.published.start) > new Date().getTime()) {
      showError(caller, backend, ErrorTranslationKeys.error_in_future);
      return;
    }

    backend.cachedBookmarkApi.bypassCache(true);
    return backend.cachedBookmarkApi
      .bookmark(
        backend,
        {
          type: AssetType.PROGRAM,
          id: program.program.programId,
        },
        false
      )
      .then(bookmark => {
        const fromBookmark = bookmark && BookmarkUtility.isPartial(backend, bookmark);
        const channel = channels.find(ch => ch.channelId === schedule.channelId);
        if (!channel) {
          showError(caller, backend, ErrorTranslationKeys.error_load_details);
          return;
        }
        const epgEvent: EpgEvent = {
          channel: channel,
          program: program.program,
          series: program.series,
          schedule: schedule,
          recording: undefined,
        };
        const streamInfo = new ReplayStreamInfo(
          epgEvent,
          fromBookmark ? StartPositionType.BOOKMARK : StartPositionType.START,
          backend,
          bookmark
        );
        return checkChannelScheduleRequirementsAndPlay(backend, caller, streamInfo, channel);
      });
  });
}

function playChannel(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
  if (!backend.isAuthorized()) {
    // todo show login
    return;
  }

  return ChannelManager.channels(backend).then(channels => {
    const fromUntil = EpgUtil.epgFromUntil(backend);
    const channel = channels.find(ch => ch.channelId === action.id);

    if (!channel) {
      showError(caller, backend, ErrorTranslationKeys.error_load_details);
      return;
    }

    return EpgApiV5.channelLineup(backend, channel.channelId, fromUntil.from, fromUntil.until).then(schedules => {
      const currentSchedule = EpgUtil.findCurrentSchedule(schedules.schedules);
      if (!currentSchedule) {
        showError(caller, backend, ErrorTranslationKeys.error_load_details);
        return;
      }

      return EpgApiV5.program(backend, currentSchedule.programId).then(program => {
        if (!program.program) {
          showError(caller, backend, ErrorTranslationKeys.error_load_details);
          return;
        }

        const epgEvent: EpgEvent = {
          channel: channel,
          program: program.program,
          series: program.series,
          schedule: currentSchedule,
          recording: undefined,
        };
        const streamInfo = new LinearStreamInfo(epgEvent, backend);
        return checkChannelScheduleRequirementsAndPlay(backend, caller, streamInfo, channel);
      });
    });
  });
}

function playSchedule(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
  if (!backend.isAuthorized()) {
    // todo show login
    return;
  }

  return Promise.all([ChannelManager.channels(backend), EpgApiV5.schedule(backend, action.id)]).then(
    ([channels, schedule]) => {
      if (schedule && timeStringToMs(schedule.schedule.published.start) > new Date().getTime()) {
        showError(caller, backend, ErrorTranslationKeys.error_in_future);
        return;
      }

      return Promise.all([
        EpgApiV5.program(backend, schedule.program.programId),
        backend.cachedBookmarkApi.bookmark(
          backend,
          {
            type: AssetType.PROGRAM,
            id: schedule.program.programId,
          },
          false
        ),
      ]).then(([program, bookmark]) => {
        const channel = channels.find(ch => ch.channelId === schedule.schedule.channelId);
        if (!channel) {
          showError(caller, backend, ErrorTranslationKeys.error_load_details);
          return;
        }

        const fromBookmark = bookmark && BookmarkUtility.isPartial(backend, bookmark);
        const epgEvent: EpgEvent = {
          channel: channel,
          program: program.program,
          series: program.series,
          schedule: schedule.schedule,
          recording: undefined,
        };
        const streamInfo = new ReplayStreamInfo(
          epgEvent,
          fromBookmark ? StartPositionType.BOOKMARK : StartPositionType.START,
          backend,
          bookmark
        );
        return checkChannelScheduleRequirementsAndPlay(backend, caller, streamInfo, channel);
      });
    }
  );
}

function playRecording(caller: Lightning.Component, action: IAssetAction, backend: Backend) {
  if (!backend.isAuthorized()) {
    // todo show login
    return;
  }

  return backend.cachedRecordingsApi.recording(backend, action.id).then(recording => {
    if (!recording) return showError(caller, backend, ErrorTranslationKeys.error_no_rights_detail_description);

    return Promise.all([
      EpgApiV5.schedule(backend, recording.schedule),
      backend.cachedBookmarkApi.bookmark(
        backend,
        {
          type: AssetType.PROGRAM,
          id: recording.program,
        },
        false
      ),
      ChannelManager.channels(backend),
    ]).then(([scheduleDetail, bookmark, channels]) => {
      if (!scheduleDetail.program) return showError(caller, backend, ErrorTranslationKeys.error_load_details);

      const channel = channels.find(channel => channel.channelId === recording.channel);
      const schedule = scheduleDetail.schedule;
      if (!channel || !schedule) {
        showError(caller, backend, ErrorTranslationKeys.error_load_details);
        return;
      }
      if (checkAdult() && checkRecordingRights(backend, caller, recording, channel, schedule)) {
        const epgEvent: EpgEvent = {
          channel: channel,
          program: scheduleDetail.program,
          series: scheduleDetail.series,
          schedule: schedule,
          recording: recording,
        };
        const fromBookmark = bookmark && BookmarkUtility.isPartial(backend, bookmark);

        const streamInfo = new NpvrStreamInfo(
          epgEvent,
          bookmark,
          fromBookmark ? StartPositionType.BOOKMARK : StartPositionType.START,
          backend
        );
        Router.navigate('play', { streamInfo });
      }
    });
  });
}

function checkRecordingRights(
  backend: Backend,
  caller: Lightning.Component,
  recording: Recording,
  channel: ChannelModel,
  schedule: IScheduleV5
) {
  if (!EpgUtil.canPlayRecording(backend, recording, schedule, channel)) {
    showError(caller, backend, ErrorTranslationKeys.error_no_rights_detail_description);
    return false;
  }
  return true;
}

function checkChannelScheduleRequirementsAndPlay(
  backend: Backend,
  caller: Lightning.Component,
  streamInfo: StreamInfo,
  channel: ChannelModel
) {
  if (checkAdult() && checkChannelScheduleRights(backend, caller, streamInfo, channel)) {
    Router.navigate('play', { streamInfo });
    return Promise.resolve();
  }
}

function checkAdult() {
  // todo check adult
  return true;
}

function checkChannelScheduleRights(
  backend: Backend,
  caller: Lightning.Component,
  streamInfo: StreamInfo,
  channel: ChannelModel
): boolean {
  const schedule = streamInfo.schedule;

  if (channel) {
    if (streamInfo.streamType && !channel.customerHasRight(streamInfo.streamType)) {
      showError(caller, backend, ErrorTranslationKeys.error_no_rights);
      return false;
    }
  }

  if (schedule && EpgUtil.scheduleHasBlackout(schedule.blackouts, BlackoutUtil.create(streamInfo.streamType))) {
    showError(caller, backend, ErrorTranslationKeys.error_blackout);
    return false;
  }

  return true;
}

function showError(caller: Lightning.Component, backend: Backend, messageKey: string) {
  caller.fireAncestors(
    '$showMessage',
    backend.translations.forKey(messageKey),
    backend.translations.forKey(ErrorTranslationKeys.error_title),
    undefined
  );
}
